import { useEffect, useState } from "react";
import { AtSign, Bell, Bookmark, MessageCircle, MessageSquareText, SquarePen } from "lucide-react";
import { Avatar, Presence } from "@/components/Avatar";
import { useChat } from "@/lib/chat";
import { api } from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import { plainText } from "@/lib/mrkdwn";

const ListShell = ({ title, action, children, testid }) => (
  <aside className="tc-sidebar" data-testid={testid}>
    <div className="tc-sidebar__header"><span className="tc-sidebar__ws" style={{ cursor: "default" }}><span>{title}</span></span>{action}</div>
    <div className="tc-sidebar__list tc-scroll" style={{ padding: "0 8px 20px" }}>{children}</div>
  </aside>
);

const Row = ({ avatar, title, meta, snippet, active, badge, onClick, testid }) => (
  <button className="tc-card" style={{ width: "100%", textAlign: "left", padding: "10px 12px", background: active ? "var(--tc-bg)" : "transparent", borderColor: active ? "var(--tc-border)" : "transparent", marginBottom: 2 }} onClick={onClick} data-testid={testid}>
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      {avatar}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}><b style={{ fontSize: 15, fontWeight: badge ? 900 : 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</b><span style={{ marginLeft: "auto", fontSize: 12, color: "var(--tc-text-muted)", whiteSpace: "nowrap" }}>{meta}</span></div>
        <div style={{ fontSize: 13, color: "var(--tc-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{snippet}</div>
      </div>
      {badge ? <span className="tc-badge">{badge}</span> : null}
    </div>
  </button>
);

export const DMsList = ({ activeId, onOpen }) => {
  const { conversations, setModal, msgState } = useChat();
  const dms = conversations.filter((c) => c.type !== "channel").sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
  return (
    <ListShell title="Direct messages" testid="dms-list" action={<button className="tc-iconbtn" title="New message" onClick={() => setModal({ type: "newMessage" })} data-testid="dms-new-message"><SquarePen size={18} /></button>}>
      {!dms.length && <div style={{ padding: 16, fontSize: 13, color: "var(--tc-text-muted)" }}>No direct messages yet.</div>}
      {dms.map((c) => {
        const last = msgState[c.id]?.messages?.slice(-1)[0];
        const other = c.type === "dm" ? c.members_info?.[0] : null;
        return <Row key={c.id} active={activeId === c.id} onClick={() => onOpen(c.id)} testid={`dm-row-${c.id}`} badge={c.unread_count}
          avatar={other ? <span className="tc-dm-avatar" style={{ width: 36, height: 36 }}><Avatar user={other} size={36} /><Presence presence={other.presence} /></span> : <span className="tc-mpdm-count" style={{ width: 36, height: 36, fontSize: 14 }}>{c.member_count - 1}</span>}
          title={c.display_name} meta={c.last_message_at ? fmtRelative(c.last_message_at) : ""} snippet={last ? `${last.user?.display_name?.split(" ")[0]}: ${plainText(last.text)}` : c.draft ? `Draft: ${c.draft}` : "Open conversation"} />;
      })}
    </ListShell>
  );
};

export const ActivityList = ({ onOpen }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/activity").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const icon = (k) => (k === "mention" ? <AtSign size={14} /> : k === "thread_reply" ? <MessageSquareText size={14} /> : <Bell size={14} />);
  return (
    <ListShell title="Activity" testid="activity-list">
      {items && !items.length && <div style={{ padding: 16, fontSize: 13, color: "var(--tc-text-muted)" }}>Mentions, thread replies and channel-wide notifications will appear here.</div>}
      {(items || []).map((m) => (
        <Row key={m.id} onClick={() => onOpen(m)} testid={`activity-row-${m.id}`}
          avatar={<Avatar user={m.user} size={36} />}
          title={<span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>{icon(m.kind)} {m.user?.display_name}</span>}
          meta={fmtRelative(m.created_at)}
          snippet={`${m.conversation?.type === "channel" ? `#${m.conversation.name}` : m.conversation?.display_name || "Direct message"}: ${plainText(m.text)}`} />
      ))}
    </ListShell>
  );
};

export const SavedList = ({ onOpen }) => {
  const [items, setItems] = useState(null);
  const { msgState } = useChat();
  useEffect(() => { api.get("/saved").then((r) => setItems(r.data)).catch(() => setItems([])); }, [msgState]);
  return (
    <ListShell title="Later" testid="saved-list">
      {items && !items.length && <div style={{ padding: 16, fontSize: 13, color: "var(--tc-text-muted)" }}><Bookmark size={14} style={{ display: "inline" }} /> Save messages for later and they'll be collected here.</div>}
      {(items || []).map((m) => (
        <Row key={m.id} onClick={() => onOpen(m)} testid={`saved-row-${m.id}`} avatar={<Avatar user={m.user} size={36} />} title={m.user?.display_name} meta={fmtRelative(m.created_at)}
          snippet={`${m.conversation?.type === "channel" ? `#${m.conversation.name}` : m.conversation?.display_name || "Direct message"}: ${plainText(m.text)}`} />
      ))}
    </ListShell>
  );
};

export const EmptyMain = ({ icon: Icon = MessageCircle, title, text }) => (
  <div className="tc-main"><div className="tc-empty" style={{ flex: 1 }}><Icon /><h3>{title}</h3><p>{text}</p></div></div>
);
